import { useState, useRef, useEffect, useCallback } from 'react';
import { Search, ChevronDown, X } from 'lucide-react';

export default function SearchableProductSelect({ products, value, onChange, testId }) {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0, width: 0 });
  const containerRef = useRef(null);
  const inputRef = useRef(null);

  const selectedProduct = products.find(p => p.id === value);

  const filtered = products.filter(p =>
    p.name.toLowerCase().includes(search.toLowerCase()) ||
    (p.category || '').toLowerCase().includes(search.toLowerCase())
  );

  const updatePosition = useCallback(() => {
    if (containerRef.current && isOpen) {
      const rect = containerRef.current.getBoundingClientRect();
      setDropdownPos({
        top: rect.bottom + 4,
        left: rect.left,
        width: rect.width
      });
    }
  }, [isOpen]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        // Also check if click is inside the fixed dropdown
        const dropdown = document.getElementById(`dropdown-${testId}`);
        if (dropdown && dropdown.contains(e.target)) return;
        setIsOpen(false);
        setSearch('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [testId]);

  useEffect(() => {
    if (isOpen) {
      updatePosition();
      window.addEventListener('scroll', updatePosition, true);
      window.addEventListener('resize', updatePosition);
      return () => {
        window.removeEventListener('scroll', updatePosition, true);
        window.removeEventListener('resize', updatePosition);
      };
    }
  }, [isOpen, updatePosition]);

  const handleSelect = (product) => {
    onChange(product.id);
    setIsOpen(false);
    setSearch('');
  };

  const handleClear = (e) => {
    e.stopPropagation();
    onChange('');
    setSearch('');
  };

  const handleOpen = () => {
    setIsOpen(true);
    setSearch('');
    setTimeout(() => inputRef.current?.focus(), 50);
  };

  return (
    <>
      <div ref={containerRef} className="relative w-full" data-testid={testId}>
        <div
          onClick={handleOpen}
          className="input-field bg-white w-full flex items-center justify-between cursor-pointer min-h-[42px] gap-2"
        >
          <span className={selectedProduct ? 'text-slate-900 truncate' : 'text-slate-400 truncate'}>
            {selectedProduct ? selectedProduct.name : 'Pilih Produk'}
          </span>
          <div className="flex items-center gap-1 flex-shrink-0">
            {selectedProduct && (
              <button onClick={handleClear} className="p-0.5 hover:bg-slate-200 rounded" type="button">
                <X size={14} className="text-slate-400" />
              </button>
            )}
            <ChevronDown size={16} className={`text-slate-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
          </div>
        </div>
      </div>

      {isOpen && (
        <div
          id={`dropdown-${testId}`}
          style={{
            position: 'fixed',
            top: dropdownPos.top,
            left: dropdownPos.left,
            width: dropdownPos.width,
            zIndex: 9999
          }}
          className="bg-white border border-slate-200 rounded-xl shadow-lg overflow-hidden"
        >
          <div className="p-2 border-b border-slate-100">
            <div className="relative">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                ref={inputRef}
                type="text"
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Cari produk..."
                className="w-full pl-8 pr-3 py-2 text-sm bg-slate-50 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500/30 focus:border-teal-400"
                data-testid={testId ? `${testId}-search` : undefined}
              />
            </div>
          </div>

          <div className="max-h-52 overflow-y-auto">
            {filtered.length === 0 ? (
              <div className="px-4 py-3 text-sm text-slate-400 text-center">Produk tidak ditemukan</div>
            ) : (
              filtered.map(p => (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => handleSelect(p)}
                  className={`w-full text-left px-4 py-2.5 text-sm hover:bg-teal-50 transition-colors flex items-center justify-between ${
                    p.id === value ? 'bg-teal-50 text-teal-700 font-medium' : 'text-slate-700'
                  }`}
                  data-testid={testId ? `${testId}-option-${p.id}` : undefined}
                >
                  <div>
                    <div className="font-medium">{p.name}</div>
                    <div className="text-xs text-slate-400">{p.category} · Stok: {p.stock} {p.unit}</div>
                  </div>
                  <span className="text-xs text-slate-400 font-mono flex-shrink-0 ml-2">
                    {new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR', minimumFractionDigits: 0 }).format(p.price)}
                  </span>
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </>
  );
}
